import { useQuery } from 'react-query';
import { SuiNotification } from '@splashthat/sui';

import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import { getVirtualSessionsApiClient } from 'lib/SplashApiClient/subclients/VirtualSessionsApiClient';
import { useGuestAccessHash } from 'utils/hooks/useGuestAccessHash';
import { useFlags } from 'utils/react-context';
import { useNotificationManager } from 'common/compounds/NotificationManager';

/**
 * Custom hook to fetch the Zoom OBF token required for embedding Zoom meetings.
 *
 * @param {Object} params - Parameters for fetching the OBF token.
 * @param {string} params.meetingId - The ID of the Zoom meeting.
 * @param {string} params.code - The authorization code, if applicable.
 * @param {Function} onError - Callback function to handle errors during the fetch operation.
 */
export default function useZoomOBFToken({ meetingId, code }, onError) {
    const { event } = useTouchpointData();
    const { guestAccessHash } = useGuestAccessHash(`zoomEmbed/${event.id}/guestAccessHash`);
    const { zoomSdkObfTokenEnabled } = useFlags();
    const { showNotification } = useNotificationManager();

    return useQuery(
        ['zoomSdkObfToken', meetingId, guestAccessHash, code],
        () =>
            getVirtualSessionsApiClient().getZoomOBFToken(event?.id, {
                'guest-access-hash': guestAccessHash,
                'meeting-id': meetingId,
                ...(code ? { code } : {}),
            }),
        {
            enabled: zoomSdkObfTokenEnabled && !!meetingId && !!guestAccessHash,
            staleTime: Infinity,
            onError: () => {
                showNotification({
                    id: window?.crypto?.randomUUID(),
                    message: "Can't connect to the session. Try again in a few minutes.",
                    variation: SuiNotification.ERROR,
                });
                if (onError) onError();
            },
        },
    );
}
